/**
 * **测量层**（架构文档 §4.2 的 ①）—— 它决定**布局**，所以必须"测一次、冻进 IR"。
 *
 * ## 为什么单独一层、而且必须能冻
 *
 * 文档里那句关键洞察：
 *
 * > 只要「测量」一致，「布局」就一致；「绘制」阶段的差异只影响像素外观。
 *
 * 而"一致"这件事**不能靠两端各测一次**去达成：各浏览器的 Canvas2D 底层不同
 * （Chromium/Firefox → Skia，Safari → Core Graphics），C++ 那边是 Skia 原生 ——
 * 同一句话量出来的宽度会差零点几个像素，整行文字的位置就跟着偏。
 *
 * 所以做法是 §6.2 那句"**让浏览器排版，冻结结果**"：
 *
 *   ① 宿主侧（浏览器）用这一层量一次 → 结果写进 IR（`advanceDots` 等）；
 *   ② 执行侧（Skia / 设备）**只按冻好的数字摆字**，不重新测量。
 *
 * ⚠️ 于是这个模块的产物是"**数字**"，不是"画法"：谁量都行，但只能量一次。
 *
 * ## ⚠️ 现在的边界（别当成已经做完）
 *
 * * **只有水平排版**：换行 / 断词还没做（thermal 标签上多行靠换行符，
 *   见 `PageEditor` 与执行侧的约定）；
 * * **字形本身不冻结**：冻的是"这一行占多宽 / 基线在哪 / 行距多少"，
 *   字形像素由各端字体决定 —— 热敏机上"字"通常是打印机内置字体或
 *   事先光栅化好的点位图，那属于输出侧（D7）。
 */

/** 一次测量要问的东西（都是**设备点**，和 IR 同一套单位） */
export interface MeasureRequest {
  text: string
  /** 字号（点） */
  sizeDots: number
  /** 行距（点）—— 0 = 跟着字号自动（`lineHeightOf` 同一条规矩） */
  lineHeightDots: number
  /** 字体标识（IR 里的 `fontId`；现在只有 `default` 一种） */
  fontId: string
}

export interface TextMetrics {
  /** 这一行**画出来占多宽**（点，已向上取整到整数点） */
  advanceDots: number
  /** 基线到行顶（点） */
  ascentDots: number
  /** 基线到行底（点） */
  descentDots: number
  /** 行距（点）—— 0 输入时由实现按字号推 */
  lineHeightDots: number
}

/** 测量器：给一段文本，回答"它占多大" */
export interface TextMeasurer {
  measure(request: MeasureRequest): TextMetrics
  /** 自己的名字（IR / 诊断里会写，方便"这组数字是谁测的"） */
  readonly id: string
}

/**
 * 字形宽度的**粗略比例**（只用于"没有画布"时的降级与纯逻辑测试）。
 *
 * ⚠️ 它**不是**字体度量：只是一个"别把结果算成 0"的确定性兜底 ——
 * 所以它测出来的数字会与浏览器略有出入，用它跑出来的 IR **不该**拿去生产。
 * 之所以仍然要它：纯逻辑验证脚本里没有画布（esbuild 打包 + node 跑），
 * 那里需要**确定性的数字**才能断言"冻结"这件事本身。
 */
const FALLBACK_GLYPH_RATIO = 0.56
/** 中日韩等宽字：比拉丁宽（0.56 那种估法会把中文算窄） */
const FALLBACK_WIDE_RATIO = 1

function hasWideGlyphs(text: string): boolean {
  /* CJK / 全角标点的粗略判据：只影响降级路径的**估算**，不影响真机 */
  return /[\u2E80-\u9FFF\uF900-\uFAFF\uFF00-\uFF60\u3000-\u303F]/.test(text)
}

/**
 * **降级测量器**（没有画布时用）。
 *
 * ⚠️ 刻意做成"能跑但明确不准"，而不是"假装准"：
 * 它只在两条路上被用到 —— 纯逻辑验证脚本、以及宿主没有 Canvas2D 的极端环境。
 * 判断标准写在 `id` 上（`'fallback'`），IR 里也会带（谁测的一眼可见）。
 */
export const fallbackTextMeasurer: TextMeasurer = {
  id: 'fallback',
  measure(request: MeasureRequest): TextMetrics {
    const lines = request.text.split('\n')
    const longest = lines.reduce((max, line) => Math.max(max, line.length), 0)
    const ratio = hasWideGlyphs(request.text) ? FALLBACK_WIDE_RATIO : FALLBACK_GLYPH_RATIO
    const advanceDots = Math.ceil(longest * request.sizeDots * ratio)
    const lineHeightDots = request.lineHeightDots > 0 ? request.lineHeightDots : request.sizeDots * 1.15
    return {
      advanceDots,
      /* 经验值：字身框上 0.8 / 下 0.2（和 `lineHeightOf` 的 1.15 配套） */
      ascentDots: Math.round(request.sizeDots * 0.8),
      descentDots: Math.round(request.sizeDots * 0.2),
      lineHeightDots,
    }
  },
}

/**
 * **浏览器测量器**：真的问一次 Canvas2D。
 *
 * ⚠️ 拿不到画布（无头 / 老环境）时**不抛错**，静默退回降级测量器，
 * 并把 `id` 改成 `'fallback'` —— 这样"这份 IR 的字宽是估的"能一路传到 IR 里。
 *
 * ⚠️ 画布**只建一次**（每次 `measureText` 都建一个 canvas 的话，
 * 大模板上会明显卡：一个页面几十个文本组件，每个都要测）。
 */
export function createCanvasTextMeasurer(dpi: number): TextMeasurer {
  let ctx: CanvasRenderingContext2D | null = null
  try {
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas')
      ctx = canvas.getContext?.('2d') ?? null
    }
  } catch {
    ctx = null
  }
  if (!ctx) return fallbackTextMeasurer
  const context = ctx

  return {
    id: 'canvas2d',
    measure(request: MeasureRequest): TextMetrics {
      const px = (dots: number) => (dots * 96) / dpi
      const fontPx = Math.max(1, px(request.sizeDots))
      context.font = `${fontPx}px sans-serif`
      const lines = request.text.split('\n')
      let widest = 0
      for (const line of lines) widest = Math.max(widest, context.measureText(line).width)
      const metrics = context.measureText('Hg')
      const lineHeightDots =
        request.lineHeightDots > 0 ? request.lineHeightDots : request.sizeDots * 1.15
      return {
        /* 画布给的是 CSS 像素，这里换回**设备点**（IR 的单位） */
        advanceDots: Math.ceil((widest * dpi) / 96),
        ascentDots: Math.round(
          ((metrics.actualBoundingBoxAscent || fontPx * 0.8) * dpi) / 96,
        ),
        descentDots: Math.round(
          ((metrics.actualBoundingBoxDescent || fontPx * 0.2) * dpi) / 96,
        ),
        lineHeightDots,
      }
    },
  }
}

/**
 * 宿主当前用的测量器（按 dpi 缓存 —— dpi 变了才需要重建，而它很少变）。
 *
 * ⚠️ 纯逻辑脚本不该碰它：那里显式传 `fallbackTextMeasurer`（见 `compilePage`
 * 的 `measure` 参数）。这就是"依赖注入"在这里的全部意义：
 * **让 IR 里那些数字是"谁测的"可指定**。
 */
const measurers = new Map<number, TextMeasurer>()
export function textMeasurerFor(dpi: number): TextMeasurer {
  const cached = measurers.get(dpi)
  if (cached) return cached
  const created = createCanvasTextMeasurer(dpi)
  measurers.set(dpi, created)
  return created
}
